import { z } from 'zod'
import { colorSchemeSchema, type ColorScheme } from '../enums/color-scheme.enum.js'
import { uuidSchema } from '../validators/params.js'

const base = {
  source: z.literal('pixelsign'),
  envelopeId: uuidSchema,
}

/**
 * What an embedded signing page posts to the page that frames it, only when
 * that page's origin is one of the organization's embed origins. They're hints
 * for the host's UI; the API and webhooks are the record.
 */
export const embedMessageSchema = z.discriminatedUnion('type', [
  /** The page has loaded the signer's session; the host can stop its own loading state. */
  z.object({ ...base, type: z.literal('ready') }).strict(),
  /** The page's content height in CSS pixels, sent on load and whenever it changes. */
  z.object({ ...base, type: z.literal('resize'), height: z.number().int().nonnegative() }).strict(),
  /** The signer has consented and the documents are showing. */
  z.object({ ...base, type: z.literal('viewed') }).strict(),
  /** This signer has signed; other signers may still be due. */
  z.object({ ...base, type: z.literal('signed') }).strict(),
  /** This signer has signed and the envelope is complete. */
  z.object({ ...base, type: z.literal('completed') }).strict(),
  /** This signer declined. */
  z.object({ ...base, type: z.literal('declined') }).strict(),
  /** The envelope expired before this signer finished. */
  z.object({ ...base, type: z.literal('expired') }).strict(),
  /** An action on the page failed; `code` is the API's error code when it gave one. */
  z.object({ ...base, type: z.literal('error'), code: z.string().nullable(), message: z.string() }).strict(),
])

export type EmbedMessage = z.infer<typeof embedMessageSchema>

/**
 * How the embed page ends once the signer signs or declines, from its
 * `ending` query parameter: `receipt` (the default) shows PixelSign's own
 * screen with downloads; `host` leaves the page empty for the host, which has
 * the message, to replace with its own.
 */
export const embedEndingSchema = z.enum(['receipt', 'host']).catch('receipt')

export type EmbedEnding = z.infer<typeof embedEndingSchema>

/**
 * Whose chrome frames the embed page, from its `chrome` query parameter:
 * `full` (the default) shows PixelSign's header and side panel; `host` leaves
 * just the documents and an action bar, for a host that shows the title
 * itself. A test envelope then says so with a TEST watermark on its pages and
 * a badge by the actions, instead of the banner.
 */
export const embedChromeSchema = z.enum(['full', 'host']).catch('full')

export type EmbedChrome = z.infer<typeof embedChromeSchema>

/**
 * The embed page's theme, from its `theme` query parameter: `light` (the
 * default) or `dark`, to match a host page in dark mode. Documents and
 * signature pads stay paper-white either way.
 */
export const embedThemeSchema = colorSchemeSchema.catch('light')

export type EmbedTheme = ColorScheme
